import {
  getCheckoutSessionById,
  getClientSecretByCheckoutSessionId,
  PaymentMethodType,
  postCreatePaymentIntent,
  postCreditCardIberiaCards,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { StandsCreditCardRequestMap } from '@/components/modules/Application/IberiaCards/modules/ShortContactForm/maps/standsCreditCardRequest.map';
import { ShortContactFormType } from '@/components/modules/Application/IberiaCards/modules/ShortContactForm/schemas/ShortContactForm.schema';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';

type PostCreditCardIberiaCardsResponse = Awaited<ReturnType<typeof postCreditCardIberiaCards>>;

const createCreditCardPaymentIntent = async (checkoutSessionId: string) => {
  const { paymentIntents } = await getCheckoutSessionById({ checkoutSessionId });
  if (paymentIntents.length > 0) {
    return;
  }

  const { clientSecret } = await getClientSecretByCheckoutSessionId({ checkoutSessionId });

  await postCreatePaymentIntent({
    checkoutSessionId,
    clientSecret,
    paymentMethodType: PaymentMethodType.CREDIT_CARD,
    paymentMethodId: uuidv4(),
  });
};

export const useCreateStandsCreditCard = () => {
  const {
    state: { iberiaCardsStandsSelection },
  } = useApplicationContext();
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();
  const { setCompleted } = useApplicationNavigation();
  const { handleSubmit } = useFormContext<ShortContactFormType>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const createCreditCard = async (contact: ShortContactFormType) => {
    if (!iberiaCardsStandsSelection) {
      console.error('Missing Iberia Cards stands selection from the previous step');
      setSnackbarNotification({ msg: `${postCreditCardIberiaCards.name}_rejected`, level: 'error' });
      return;
    }

    setLoading(true);

    let response: PostCreditCardIberiaCardsResponse;
    try {
      response = await postCreditCardIberiaCards(
        StandsCreditCardRequestMap.toRequest(iberiaCardsStandsSelection, contact),
      );
    } catch (error) {
      setSnackbarNotification({ msg: `${postCreditCardIberiaCards.name}_rejected`, level: 'error', error });
      setLoading(false);
      return;
    }

    try {
      await createCreditCardPaymentIntent(response.checkoutSessionId);
    } catch (error) {
      console.error('Could not create the credit card payment intent for the stands checkout session', error);
    }

    setCompleted();
    // The continue param opens the payment intent on arrival, so the agent goes straight into the flow with the client
    navigate(
      `${generatePath(DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL, {
        checkoutSessionId: response.checkoutSessionId,
      })}?continue=true`,
    );
  };

  const onSubmit = () => {
    handleSubmit(createCreditCard)();
  };

  return {
    onSubmit,
    loading,
  };
};
